import ForgotPasswordPage from "@/pages/auth/ForgotPassword";
import NewPassword from "@/pages/auth/NewPassword";
import AuthRoute from "@/pages/AuthRoute";
import ChatPage from "@/pages/ChatPage";
import DataRoutes from "@/pages/data/DataRoutes";
import { ExplorePage } from "@/pages/Explore";
import { PlaysPage } from "@/pages/explore/PlaysPage";
import { FlightsPage } from "@/pages/Flights";
import {
  ForecastPage,
  RetailForecastPage,
} from "@/pages/forecast/ForecastPage";
import HomePage from "@/pages/HomePage";
import InvitationPage from "@/pages/InvitationPage";
import AppLayout from "@/pages/layouts/AppLayout";
import RetailLayout from "@/pages/layouts/RetailLayout";
import LoginPage from "@/pages/LoginPage";
import NotificationsPage from "@/pages/NotificationsPage";
import ProtectedRoute from "@/pages/ProtectedRoute";
// SSO(448) — remove with the SSO feature
import SSOCallback from "@/pages/auth/SSOCallback";
import SSOLogin from "@/pages/auth/SSOLogin";
import ResetPage from "@/pages/ResetPage";
import RetailHome from "@/pages/retail/RetailHome";
import MarketDemandPage from "@/pages/retail/MarketDemandPage";
import MembersPage from "@/pages/setup/MembersPage";
import ManagePlaysPage from "@/pages/setup/PlaysPage";
import TeamsPage from "@/pages/setup/TeamsPage";
import { RetailFlightsPage } from "@/pages/retail/RetailFlightsPage";
import { AirportProvider, useAirport } from "@/stores/AirportContext";
import { NotificationsProvider } from "@/stores/NotificationsContext";
import SessionDataProvider, {
  useSessionData,
} from "@/stores/SessionDataContext";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useState } from "react";
import {
  Route,
  BrowserRouter as Router,
  Routes,
  Navigate,
} from "react-router-dom";
import { PlayDetailPage } from "./pages/PlayDetailPage";
import ManageAirportPage from "./pages/setup/AirportPage";

// TODO: When AI chat is ready for public release:
//   1. Delete this DevOnlyRoute component
//   2. Replace <DevOnlyRoute><ChatPage /></DevOnlyRoute> with just <ChatPage /> in both
//      AirportModeRoutes and BrandModeRoutes below
//   To open chat to all users at an airport: clear Airport.ai_chat_beta_emails in Django admin.
function DevOnlyRoute({
  children,
  retail = false,
}: {
  children: React.ReactNode;
  retail?: boolean;
}) {
  const { aiChatEnabled, retailAiChatEnabled } = useAirport();
  const enabled = retail ? retailAiChatEnabled : aiChatEnabled;

  if (!enabled) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

function AirportModeRoutes() {
  return (
    <Routes>
      <Route path="/" element={<AppLayout />}>
        <Route index element={<HomePage />} />
        <Route path="explore" element={<ExplorePage />} />
        <Route path="flights" element={<FlightsPage />} />
        <Route path="plays" element={<PlaysPage />} />
        <Route path="plays/:playId" element={<PlayDetailPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route
          path="chat"
          element={
            <DevOnlyRoute>
              <ChatPage />
            </DevOnlyRoute>
          }
        />
        <Route path="data/*" element={<DataRoutes />}></Route>
      </Route>
      <Route path="/forecast" element={<AppLayout />}>
        <Route index element={<ForecastPage />} />
      </Route>
      <Route path="/setup" element={<AppLayout />}>
        <Route path="rules" element={<ManagePlaysPage />} />
        <Route path="teams" element={<TeamsPage />} />
        <Route path="members" element={<MembersPage />} />
        <Route path="airport" element={<ManageAirportPage />} />
      </Route>
    </Routes>
  );
}

function BrandModeRoutes() {
  return (
    <Routes>
      <Route path="/" element={<RetailLayout />}>
        <Route index element={<RetailHome />}></Route>
      </Route>
      <Route path="/store/:store" element={<RetailLayout />}>
        <Route index element={<RetailHome />} />
      </Route>
      <Route path="/market-demand" element={<RetailLayout />}>
        <Route index element={<MarketDemandPage />} />
      </Route>
      <Route path="/market-demand/store/:store" element={<RetailLayout />}>
        <Route index element={<MarketDemandPage />} />
      </Route>
      <Route path="/forecast" element={<RetailLayout />}>
        <Route index element={<RetailForecastPage />} />
      </Route>
      <Route path="/notifications" element={<RetailLayout />}>
        <Route index element={<NotificationsPage />} />
      </Route>
      <Route path="/plays" element={<RetailLayout />}>
        <Route index element={<PlaysPage />} />
      </Route>
      <Route path="/flights" element={<RetailLayout />}>
        <Route index element={<RetailFlightsPage />} />
      </Route>
      <Route path="/plays/:playId" element={<RetailLayout />}>
        <Route index element={<PlayDetailPage />} />
      </Route>
      <Route path="/chat" element={<RetailLayout />}>
        <Route
          index
          element={
            <DevOnlyRoute retail>
              <ChatPage />
            </DevOnlyRoute>
          }
        />
      </Route>
      <Route path="/setup" element={<RetailLayout />}>
        <Route path="rules" element={<ManagePlaysPage />} />
        <Route path="teams" element={<TeamsPage />} />
        <Route path="members" element={<MembersPage />} />
      </Route>
    </Routes>
  );
}

function AppRoutes() {
  const { isBrand, isAirport, session_type } = useSessionData();

  if (isBrand || session_type === "retail_all") {
    return <BrandModeRoutes />;
  }

  if (isAirport) {
    return <AirportModeRoutes />;
  }

  return null; // Return null if neither condition is met
}

function App() {
  const [reactQueryClient] = useState(
    new QueryClient({
      defaultOptions: {
        queries: {
          refetchOnWindowFocus: false,
          refetchOnMount: false,
        },
      },
    })
  );

  return (
    <QueryClientProvider client={reactQueryClient}>
      <Router>
        <Routes>
          <Route element={<AuthRoute />}>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/forgot" element={<ForgotPasswordPage />} />
            <Route path="/auth/new-password" element={<NewPassword />} />
            {/* SSO(448) — remove with the SSO feature */}
            <Route path="/login/sso" element={<SSOLogin />} />
          </Route>
          <Route path="/invitation" element={<InvitationPage />} />
          {/* ─── SSO(448) begin — remove with the SSO feature ───
              Outside both guards on purpose: AuthRoute would bounce a user who
              still holds a stale cookie straight to "/", discarding the sign-in,
              and ProtectedRoute cannot apply because no token exists yet. */}
          <Route path="/auth/callback" element={<SSOCallback />} />
          {/* ─── SSO(448) end ─── */}
          <Route
            element={
              <SessionDataProvider>
                <AirportProvider>
                  <NotificationsProvider>
                    <ProtectedRoute />
                  </NotificationsProvider>
                </AirportProvider>
              </SessionDataProvider>
            }
          >
            <Route path="/reset" element={<ResetPage />} />
            <Route path="/*" element={<AppRoutes />} />
          </Route>
        </Routes>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
